import { useMutation, useQueryClient } from '@tanstack/react-query';
import { documentWorkflowControllerCloseDocumentWithoutSignatureMutation } from '@dealstack/shared/lib/backendClient/@tanstack/react-query.gen';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { buildInvestmentProgrammeDocumentQueryKey } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/documentQueryKeys';
import { getDocumentWorkflowEventsQueryKey } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/Documents/hooks/useDocumentWorkflowEvents';

interface Options {
	dealId: string;
	programmeId: string;
	documentWorkflowInstanceId: string;
	onSuccess?: () => void;
}

/**
 * Closes a document workflow instance the investor will not sign. `dealId` is
 * not a path param on this route (the backend client injects it as the
 * `deal-id` header) but is still needed here to invalidate the right document
 * query key.
 */
export const useCloseDocumentWithoutSignature = ({
	dealId,
	programmeId,
	documentWorkflowInstanceId,
	onSuccess,
}: Options) => {
	const tMipManage = useTranslationMipManage();
	const queryClient = useQueryClient();

	const mutation = useMutation({
		...documentWorkflowControllerCloseDocumentWithoutSignatureMutation(),
		onSuccess: async () => {
			showToastV2({
				state: 'success',
				title: tMipManage('documents_page.close_document.toast.success'),
			});
			await Promise.all([
				queryClient.invalidateQueries({
					queryKey: buildInvestmentProgrammeDocumentQueryKey({
						dealId,
						programmeId,
						documentWorkflowInstanceId,
					}),
				}),
				queryClient.invalidateQueries({
					queryKey: getDocumentWorkflowEventsQueryKey({
						programmeId,
						documentWorkflowInstanceId,
					}),
				}),
			]);
			onSuccess?.();
		},
		onError: () => {
			showToastV2({
				state: 'error',
				title: tMipManage('documents_page.close_document.toast.error'),
			});
		},
	});

	return {
		close: (reason: string) =>
			mutation.mutate({
				path: { programmeId, documentWorkflowInstanceId },
				body: { reason },
			}),
		isPending: mutation.isPending,
	};
};
